import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pageToolbar } from '../../tabPalette';   // the page toolbar's bubble (report 76a2c198) — for a caller that passes `inset`

// ── ⛔ THE ONE NON-WRAPPING TOOLBAR (owner's rule, "工具栏永不折行 · 单点 PeakToolbar") ──────────
// Lifted out of components/Peak/index.tsx unchanged on 2026-10-19, for the same reason MailBadge.tsx
// was lifted out of Peak's local SentBadge: the rule is the house's, and a toolbar that lives inside
// the invoice list can only be obeyed by the invoice list. The second screen that needs it is the
// interactive general ledger (CompanyWorkspace/LedgerAccounts.tsx), and the alternative — a second
// measure-and-fold toolbar written beside this one — is exactly the drift the single-point rule
// exists to prevent. Peak's three lists import it from here and behave as they did.
//
// ⛔ NOT ONE LINE OF BEHAVIOUR CHANGED IN THE MOVE. Same props, same measuring, same grey band, same
// trailing More. The comments below are the originals.

// ── The list toolbars: ONE row, never two (owner 2026-09-25) ─────────────────
// Invoice's "Payment reminder" and "Monthly billing" dropped onto a second row as soon as the
// filters filled the width — a whole row of the list spent on two buttons, sitting where nobody
// looks for them. The row no longer wraps. The filters keep their natural width and scroll inside
// their own box if the window gets small; the ACTIONS at the end are measured, and as many as fit
// stay as buttons while the rest fold into one "More" menu at the end of the row. Quotation and
// Receipt are built from the same bar, so the rule holds wherever an action is added next.
//
// Measured rather than switched at a breakpoint: the filters' width depends on how many firms this
// user can see (the tabs held unpaid / expired counts too, until report 38417c66 took them off), so
// no fixed number would stay right for long. Every width is read off the elements themselves — a
// folded action stays in the DOM, parked off-screen, so it can still be measured — and the count
// only ever settles on what actually fits.
export type BarMenuItem = { key: string; label: string; hint?: string; icon?: string; danger?: boolean; busy?: boolean; onClick: () => void };
export type BarAction = {
  key: string; label: string; icon: string;
  tone?: 'accent' | 'amber';
  title?: string; busy?: boolean;
  onClick?: () => void;      // a plain action…
  menu?: BarMenuItem[];      // …or one that opens a menu of its own
};

const BAR_TONE: Record<string, { border: string; color: string }> = {
  accent: { border: 'var(--accent)', color: 'var(--accent)' },
  amber: { border: '#D97706', color: '#B45309' },
};

// One row of a dropdown — the label, and underneath it what choosing it will actually do. The
// descriptions are why the month-level actions live in a menu at all: a create-only action and a
// rebuild-everything action must never be two look-alike buttons side by side.
function BarMenuRow({ m, last, onDone }: { m: BarMenuItem; last?: boolean; onDone: () => void }) {
  return (
    <button onClick={() => { if (m.busy) return; onDone(); m.onClick(); }} disabled={m.busy}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: last ? 'none' : '1px solid var(--border)', background: 'transparent', cursor: m.busy ? 'default' : 'pointer', opacity: m.busy ? 0.6 : 1 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: m.danger ? '#B45309' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {(m.icon || m.busy) && <i className={`ti ${m.busy ? 'ti-loader-2' : m.icon}`} style={{ fontSize: 14, color: m.danger ? '#B45309' : 'var(--accent)', animation: m.busy ? 'spin 0.9s linear infinite' : undefined }} />}{m.label}
      </div>
      {m.hint && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.45 }}>{m.hint}</div>}
    </button>
  );
}

const barPanel: React.CSSProperties = { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, width: 330, background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' };

function BarActionButton({ a }: { a: BarAction }) {
  const [open, setOpen] = useState(false);
  const tone = BAR_TONE[a.tone || 'accent'];
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => { if (a.busy) return; if (a.menu) setOpen(o => !o); else a.onClick?.(); }} title={a.title} disabled={a.busy}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${tone.border}`, background: 'transparent', color: tone.color, cursor: a.busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, opacity: a.busy ? 0.6 : 1, whiteSpace: 'nowrap' }}>
        <i className={`ti ${a.busy ? 'ti-loader-2' : a.icon}`} style={{ fontSize: 14, animation: a.busy ? 'spin 0.9s linear infinite' : undefined }} />
        {a.label}
        {a.menu && <i className="ti ti-chevron-down" style={{ fontSize: 12 }} />}
      </button>
      {a.menu && open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={barPanel}>
            {a.menu.map((m, i) => <BarMenuRow key={m.key} m={m} last={i === a.menu!.length - 1} onDone={() => setOpen(false)} />)}
          </div>
        </>
      )}
    </div>
  );
}

// The folded actions. A plain one becomes a row; one that carries its own menu keeps that menu's
// rows, under its name — nothing is reduced to a bare label on the way in here.
function BarOverflow({ actions, innerRef }: { actions: BarAction[]; innerRef: React.RefObject<HTMLDivElement> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rows: React.ReactNode[] = [];
  actions.forEach((a, ai) => {
    if (a.menu) {
      rows.push(
        <div key={a.key} style={{ borderTop: ai ? '1px solid var(--border)' : 'none' }}>
          <div style={{ padding: '9px 14px 2px', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{a.label}</div>
          {a.menu.map((m, i) => <BarMenuRow key={m.key} m={m} last={i === a.menu!.length - 1} onDone={() => setOpen(false)} />)}
        </div>,
      );
    } else {
      rows.push(<BarMenuRow key={a.key} m={{ key: a.key, label: a.label, icon: a.icon, hint: a.title, busy: a.busy, onClick: () => a.onClick?.() }} last={ai === actions.length - 1} onDone={() => setOpen(false)} />);
    }
  });
  // ⛔ A folded download must still spin (the house rule: every download shows it is working). The
  // menu closes as its row is chosen, so the row alone cannot carry it — the More button that now
  // stands where the action stood spins for as long as a folded action is busy.
  // (Review 2026-11-02) One carrying a menu too: it spins as a button when it stands, so folded it was the one download that did not.
  const busy = actions.some(a => a.busy);
  return (
    <div ref={innerRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} title={t('eml_nav_more')} aria-label={t('eml_nav_more')} aria-busy={busy || undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
        <i className={`ti ${busy ? 'ti-loader-2' : 'ti-dots'}`} style={{ fontSize: 15, animation: busy ? 'spin 0.9s linear infinite' : undefined }} aria-hidden />{t('eml_nav_more')}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={barPanel}>{rows}</div>
        </>
      )}
    </div>
  );
}

const BAR_OFFSCREEN: React.CSSProperties = { position: 'absolute', left: -99999, top: 0, visibility: 'hidden', pointerEvents: 'none' };

export default function PeakToolbar({ filters, right, actions = [], inset }: { filters: React.ReactNode; right?: React.ReactNode; actions?: BarAction[]; inset?: number }) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const filRef = useRef<HTMLDivElement | null>(null);
  const rigRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const n = actions.length;
  const [shown, setShown] = useState(n);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const GAP = 8, PAD = 32;   // the row's own left+right padding
    const measure = () => {
      const avail = row.clientWidth - PAD;
      // scrollWidth, not offsetWidth: the filters box may already be scrolling, and what matters is
      // the width it WANTS. Below that the box scrolls on its own and the row still holds one line.
      const base = (filRef.current?.scrollWidth || 0) + (rigRef.current?.offsetWidth || 0) + GAP * 2;
      const w = itemRefs.current.slice(0, n).map(el => el?.offsetWidth || 0);
      const moreW = moreRef.current?.offsetWidth || 86;
      const need = (k: number) => base + w.slice(0, k).reduce((sum, x) => sum + x + GAP, 0) + (k < n ? moreW + GAP : 0);
      let k = n;
      while (k > 0 && need(k) > avail) k--;
      setShown(prev => (prev === k ? prev : k));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    if (filRef.current) ro.observe(filRef.current);
    return () => ro.disconnect();
  }, [n]);

  // The row is the light grey, --c-gray-50, on Quotation, Invoice and Receipt, as File Storage's toolbar band is and
  // every top-menu page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns that
  // same grey then the rest of the page below this menu has a white background"). It was the page's white,
  // --bg-surface; the list under it stays white. Its line is a shade darker, --border-strong where it was --border, as
  // is the top menu's over it (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on
  // all page menus like this").
  // `inset` (one-line merge 2026-11-03): Quotation, Invoice and Receipt pass their CARD_INSET, and the row is a bubble,
  // as every page toolbar on that line became while this toolbar was being lifted out here (Calem, report 76a2c198,
  // 2nd opening — see pageToolbar): the document cards' 10px corners and a --border-strong line all the way round
  // where the line ran under it alone, `inset` in from the list's edges so its ends stand where the cards' do. What the
  // row holds, and how much of it folds away on a narrow window (see measure), are untouched — the inset takes 2×inset
  // off the row's width, which is simply that much less for the actions to fit in. A caller that passes no inset (the
  // ledger, the trial balance, the workspace panes) keeps the band with its line under it, as it was.
  return (
    <div ref={rowRef} style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 8, padding: '8px 16px', ...(inset === undefined ? { borderBottom: '1px solid var(--border-strong)' } : pageToolbar(inset)), background: 'var(--c-gray-50)' }}>
      <div ref={filRef} className="pk-bar-filters" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 1, overflowX: 'auto' }}>{filters}</div>
      <div style={{ flex: 1, minWidth: 0 }} />
      {right && <div ref={rigRef} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{right}</div>}
      {n > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {actions.map((a, i) => (
            <div key={a.key} ref={el => { itemRefs.current[i] = el; }} style={i < shown ? undefined : BAR_OFFSCREEN}>
              <BarActionButton a={a} />
            </div>
          ))}
          {shown < n && <BarOverflow actions={actions.slice(shown)} innerRef={moreRef} />}
        </div>
      )}
    </div>
  );
}

